import { useEffect, useRef, useState } from "react";
import { createPurchaseOrder, deletePurchaseOrder, fetchPOItems, fetchPurchaseOrders } from "../api";
import type { POItem } from "../api";
import type { AppConfig, PurchaseOrder } from "../types";

function fmt(v: string) {
  const n = parseFloat(v);
  return isNaN(n) ? v : n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function statusColor(s: string) {
  if (s === "open") return "var(--good)";
  if (s === "closed") return "var(--text-dim)";
  return "var(--warn)";
}

export default function PurchaseOrders({ cfg }: { cfg: AppConfig }) {
  const [items, setItems] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<PurchaseOrder | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  function load() {
    setLoading(true);
    fetchPurchaseOrders(cfg)
      .then(setItems)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(load, [cfg]);

  async function handleDelete(po: PurchaseOrder) {
    if (!confirm(`Delete PO from "${po.source}"?`)) return;
    try {
      await deletePurchaseOrder(cfg, po.id);
      setSelected(null);
      load();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  return (
    <>
      <div className="view-header">
        <span className="view-title">Purchase Orders</span>
        <span className="view-count">{items.length}</span>
        <button className="btn btn-primary" style={{ marginLeft: "auto" }} onClick={() => setShowCreate(true)}>
          + New PO
        </button>
      </div>

      <div className="table-wrap">
        {loading && <div className="state-container"><div className="spinner" /></div>}
        {err && <div className="state-container"><p style={{ color: "var(--bad)" }}>{err}</p></div>}
        {!loading && !err && (
          <table>
            <thead>
              <tr>
                <th>Source</th>
                <th>Seller</th>
                <th>Date</th>
                <th style={{ textAlign: "right" }}>Item Total</th>
                <th style={{ textAlign: "right" }}>Shipping</th>
                <th style={{ textAlign: "right" }}>Fees</th>
                <th style={{ textAlign: "right" }}>Total Paid</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((po) => (
                <tr key={po.id} onClick={() => setSelected(po)} className={selected?.id === po.id ? "selected" : ""}>
                  <td style={{ fontWeight: 500 }}>{po.source}</td>
                  <td style={{ color: "var(--text-dim)" }}>{po.seller ?? "—"}</td>
                  <td>{po.purchased_at.slice(0, 10)}</td>
                  <td className="td-money">{fmt(po.item_total)}</td>
                  <td className="td-money">{fmt(po.shipping_paid)}</td>
                  <td className="td-money">{fmt(po.platform_fees)}</td>
                  <td className="td-money" style={{ fontWeight: 600 }}>{fmt(po.total_paid)}</td>
                  <td>
                    <span style={{ fontSize: 11, color: statusColor(po.status) }}>{po.status}</span>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: "center", color: "var(--text-dim)", padding: 32 }}>No purchase orders yet</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <>
          <div className="drawer-backdrop" onClick={() => setSelected(null)} />
          <div className="drawer">
            <div className="drawer-header">
              <h2>PO Detail</h2>
              <button className="drawer-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="drawer-body">
              <div className="drawer-section">
                <span className="drawer-section-title">Details</span>
                {[
                  ["ID", <span className="field-mono">{selected.id}</span>],
                  ["Source", selected.source],
                  ["Seller", selected.seller ?? "—"],
                  ["URL", selected.source_url ? <a href={selected.source_url} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>{selected.source_url}</a> : "—"],
                  ["Date", selected.purchased_at.slice(0, 10)],
                  ["Item Total", fmt(selected.item_total)],
                  ["Shipping", fmt(selected.shipping_paid)],
                  ["Platform Fees", fmt(selected.platform_fees)],
                  ["Total Paid", <strong>{fmt(selected.total_paid)}</strong>],
                  ["Status", selected.status],
                  ["Notes", selected.notes ?? "—"],
                ].map(([label, value]) => (
                  <div key={String(label)} className="field-row">
                    <span className="field-label">{label}</span>
                    <span className="field-value">{value}</span>
                  </div>
                ))}
              </div>
              <POItemList cfg={cfg} poId={selected.id} />
              <button
                className="btn"
                style={{ color: "var(--bad)", borderColor: "var(--bad)" }}
                onClick={() => handleDelete(selected)}
              >
                Delete PO
              </button>
            </div>
          </div>
        </>
      )}

      {showCreate && (
        <CreatePOModal
          cfg={cfg}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </>
  );
}

function POItemList({ cfg, poId }: { cfg: AppConfig; poId: string }) {
  const [items, setItems] = useState<POItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPOItems(cfg, poId)
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cfg, poId]);

  if (loading) return <div className="spinner" style={{ margin: "8px 0" }} />;
  if (items.length === 0) return null;

  const modeBadgeColor: Record<string, string> = {
    sealed: "rgba(168,85,247,0.15)",
    singles: "rgba(52,211,153,0.12)",
    graded: "rgba(79,140,255,0.12)",
  };
  const modeTextColor: Record<string, string> = {
    sealed: "#c084fc",
    singles: "var(--good)",
    graded: "var(--accent)",
  };

  return (
    <div className="drawer-section">
      <span className="drawer-section-title">Linked Items ({items.length})</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {items.map((it) => (
          <div key={it.id} style={{ background: "var(--bg-elev-2)", borderRadius: 5, padding: "7px 10px", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ background: modeBadgeColor[it.mode] ?? "transparent", color: modeTextColor[it.mode] ?? "var(--text-dim)", borderRadius: 3, padding: "1px 6px", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
              {it.mode}
            </span>
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</span>
            <span style={{ color: "var(--text-dim)", flexShrink: 0 }}>{it.game}</span>
            <span style={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
              {parseFloat(it.cost_basis).toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </span>
            <span style={{ color: it.status === "in_stock" ? "var(--good)" : "var(--text-dim)", fontSize: 10, flexShrink: 0 }}>{it.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreatePOModal({ cfg, onClose, onCreated }: { cfg: AppConfig; onClose: () => void; onCreated: () => void }) {
  const [source, setSource] = useState("");
  const [seller, setSeller] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [purchasedAt, setPurchasedAt] = useState(new Date().toISOString().slice(0, 10));
  const [itemTotal, setItemTotal] = useState("");
  const [shipping, setShipping] = useState("0");
  const [fees, setFees] = useState("0");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      await createPurchaseOrder(cfg, {
        source,
        seller: seller || null,
        source_url: sourceUrl || null,
        purchased_at: purchasedAt,
        item_total: itemTotal,
        shipping_paid: shipping,
        platform_fees: fees,
        notes: notes || null,
      });
      onCreated();
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer" ref={ref}>
        <div className="drawer-header">
          <h2>New Purchase Order</h2>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>
        <div className="drawer-body">
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Source *</label>
              <input className="form-input" value={source} onChange={(e) => setSource(e.target.value)} placeholder="eBay, TCGPlayer, LGS…" required />
            </div>
            <div className="form-group">
              <label className="form-label">Seller</label>
              <input className="form-input" value={seller} onChange={(e) => setSeller(e.target.value)} placeholder="Username or store name" />
            </div>
            <div className="form-group">
              <label className="form-label">Source URL</label>
              <input className="form-input" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…" />
            </div>
            <div className="form-group">
              <label className="form-label">Purchase Date *</label>
              <input className="form-input" type="date" value={purchasedAt} onChange={(e) => setPurchasedAt(e.target.value)} required />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Item Total *</label>
                <input className="form-input" value={itemTotal} onChange={(e) => setItemTotal(e.target.value)} placeholder="0.00" required />
              </div>
              <div className="form-group">
                <label className="form-label">Shipping</label>
                <input className="form-input" value={shipping} onChange={(e) => setShipping(e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Fees</label>
                <input className="form-input" value={fees} onChange={(e) => setFees(e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            {err && <p style={{ color: "var(--bad)", fontSize: 12 }}>{err}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Create PO"}</button>
              <button className="btn" type="button" onClick={onClose}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
